import { useState } from "react";
import { ConfirmDialog } from "@/components/common/Dialog";
import { showSuccessToast } from "@/hooks/use-toast";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";
import { regenerateWebhookSecret } from "@/api/webhook";

export type ConfirmWebhookSecretGenerationDialogProps = {
  id: number | undefined;
  callBack: () => Promise<void>;
  dialogOpenState?: boolean;
  onClose: () => void;
};

export const ConfirmWebhookSecretGenerationDialog = ({
  id,
  callBack,
  dialogOpenState,
  onClose,
}: ConfirmWebhookSecretGenerationDialogProps) => {
  const [dialogOpen, setDialogOpen] = useState(dialogOpenState || false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    setError(null);
  };

  const handleConfirmRegenerateWebhookSecret = async () => {
    setLoading(true);
    setError(null);
    try {
      await regenerateWebhookSecret(id);
      showSuccessToast("Webhook secret regenerated successfully.");
      setDialogOpen(false);
      await callBack();
      onClose();
    } catch (err) {
      setError("Failed to regenerate webhook secret.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay
        isVisible={loading}
        message="Regenerating Webhook Secret..."
      />
      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        triggerText="Regenerate Secret"
        triggerVariant="outline"
        disableTrigger={false}
        triggerTitle={"Regenerate Webhook Secret"}
        title="Regenerate Webhook Secret"
        description={
          <>
            Regenerating the webhook secret will invalidate the secret instantly
            and any outstanding requests waiting to be processed may fail.
            <br />
            <br />
            Are you sure you want to continue?
            <br />
            <strong>This action cannot be undone!</strong>
            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
          </>
        }
        confirmText="Regenerate"
        cancelText="Cancel"
        loading={loading}
        onConfirm={handleConfirmRegenerateWebhookSecret}
        disableConfirm={loading}
      />
    </>
  );
};
